// Remix
import type {
  LinksFunction,
  LoaderFunction,
  MetaFunction } from
"@remix-run/node";
import { json } from "@remix-run/node";
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation } from
"@remix-run/react";
import { TwicInstall } from "@twicpics/components/react";
// Providers & Hooks
import { useTranslation } from "react-i18next";
// Components
import { redirect } from "react-router-dom";
import { useChangeLanguage } from "remix-i18next/react";
import { isPreviewMode } from "utils/preview-mode.server";
import { createContentfulClient } from "~/apollo/index.server";
import { NAV_QUERY } from "~/apollo/queries/nav";
import { GoogleTagManagerNoScript } from "~/components/utils/GoogleTagManager";
import {
  getEnv,
  GLOBAL_CACHE_SECONDS,
  SALESFORCE_HOST_SCRIPT_SRC } from
"~/config";
import { i18n } from "~/i18n.server";
// Styles
import tailwindStylesheetUrl from "~/tailwind/tailwind.css";
import fontsStyleSheetUrl from "../styles/fonts.css";
import { isAuthorized } from "./basicAuth.server";
import { Nav } from "./components/nav";
import NavContentful from "./components/nav/NavContentful";
import { PreviewBanner } from "./components/preview-banner/Preview-Banner";
import { Footer } from "./components/shared";
import { HotJar } from "./components/utils/HotJar";
import { TrackPageViews } from "./components/utils/useTrackPageViews";
import { getRedirectedRoute } from "./contentful/index.server";
import RootProvider from "./contexts/RootProvider";
import { NavQuery, NavQueryVariables } from "./gql/graphql";
import { pathedRoutes } from "./other-routes.server";

export const links: LinksFunction = () => {
  return [
  { rel: "stylesheet", href: tailwindStylesheetUrl },
  { rel: "stylesheet", href: fontsStyleSheetUrl }];

};

export const meta: MetaFunction = () => {
  /**
   * This is the root meta tag for the project, and is applied to every page.
   * You'll probably want to customize this for your project.
   * See: https://ogp.me/
   */
  const description = "The Tony Blair Institute for Global Change";

  return [
  { charset: "utf-8" },
  { title: "Tony Blair Institute" },
  { name: "keywords", content: "Tony Blair,TBI,blog,research" },
  { name: "twitter:creator", content: "@InstituteGC" },
  { name: "twitter:site", content: "@InstituteGC" },
  { name: "twitter:title", content: "Tony Blair Institute" },
  { name: "twitter:description", content: description },
  { name: "viewport", content: "width=device-width,initial-scale=1" }];

};

type LoaderData = {
  authorized: boolean;
  GLOBALS: string;
  GTM_ID: string | null;
  PUBLICLY_AVAILABLE_ORIGIN: string;
  locale: string;
  isInPreview: boolean;
  hasParameters: boolean;
  navigation: NavQuery;
};

export const loader: LoaderFunction = async ({ request }) => {
  // =============================================
  // Basic Auth
  // =============================================
  if (!isAuthorized(request)) {
    return json({ authorized: false }, { status: 401 });
  }

  if (pathedRoutes[new URL(request.url).pathname]) {
    return new Response();
  }

  const locale = await i18n.getLocale(request);

  // These are set in contentful. Check there for redirects.
  const url = new URL(request.url);
  const redirectedRoute = await getRedirectedRoute(url.pathname, locale);

  if (redirectedRoute && redirectedRoute.fields.to) {
    return redirect(
      `${redirectedRoute.fields.insight ? "/insights/news" : ""}${
      redirectedRoute.fields.to}`,

      redirectedRoute.fields.temporary ? 302 : 301
    );
  }
  //check for params
  const preview = await isPreviewMode(request);
  const hasParameters = [...url.searchParams.keys()].length > 0;

  // --- Contentful (server-only Apollo) ---
  // const preview = url.searchParams.get("preview") === "1";
  const apollo = createContentfulClient();
  const { data, error } = await apollo.query<NavQuery, NavQueryVariables>({
    query: NAV_QUERY
  });

  // Log the error to help debug on Vercel
  if (error) {
    console.error("Navigation query error:", error);
  }

  // More defensive fallback
  const navigation = data?.navigationCollection ?
  data :
  { navigationCollection: { items: [] } };

  return json<LoaderData>(
    {
      authorized: true,
      GTM_ID: getEnv("GTM_ID", { default: null }),
      PUBLICLY_AVAILABLE_ORIGIN: getEnv("PUBLICLY_AVAILABLE_ORIGIN"),
      locale,
      isInPreview: preview,
      GLOBALS: JSON.stringify({
        SENTRY_DSN: getEnv("SENTRY_DSN", { default: null }),
        ALGOLIA_INDEX_PREFIX: getEnv("ALGOLIA_INDEX_PREFIX", { default: "" }),
        RECAPTCHA_SITE_KEY: getEnv("RECAPTCHA_SITE_KEY", { default: null })
      }),
      hasParameters: hasParameters,
      navigation: navigation ?? { navigationCollection: { items: [] } }
    },
    {
      status: 200,
      headers: { "cache-control": `public, s-max-age=${GLOBAL_CACHE_SECONDS}` }
    }
  );
};

export default function App() {
  let {
    locale,
    GLOBALS,
    authorized,
    GTM_ID,
    PUBLICLY_AVAILABLE_ORIGIN,
    isInPreview,
    hasParameters,
    navigation
  } = useLoaderData<LoaderData>();
  let { i18n } = useTranslation();

  const isContentfulNavActive =
  navigation?.navigationCollection?.items[0]?.isActive ?? false;

  // This hook will change the i18n instance language to the current locale
  // detected by the loader, this way, when we do something to change the
  // language, this locale will change and i18next will load the correct
  // translation files
  useChangeLanguage(locale);

  const { pathname } = useLocation();
  const canonicalUrl = `${PUBLICLY_AVAILABLE_ORIGIN}${pathname}`;
  const breakpoints = {
    xs: 320,
    sm: 375,
    md: 768,
    lg: 1024,
    xl: 1440,
    "2xl": 1536
  };

  if (!authorized) {
    return <p>Unauthorized</p>;
  }

  return (
    <html lang={locale} className="h-full" dir={i18n.dir()}>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <Meta />
        {hasParameters && <meta name="robots" content="noindex" />}
        <Links />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="preconnect" href="https://mktdplp102cdn.azureedge.net" />
        <link rel="preconnect" href="https://images.ctfassets.net" />
        {/* Salesforce embed script as provided */}
        <script src={SALESFORCE_HOST_SCRIPT_SRC} async />
      </head>
      <body>
        <RootProvider>
          <TwicInstall
            domain="https://tbi-assets.twic.pics"
            breakpoints={breakpoints} />
          
          <GoogleTagManagerNoScript gtmId={GTM_ID} />
          <HotJar />
          <TrackPageViews />
          {/* <WelcomeTransition /> */}
          {isInPreview && <PreviewBanner />}
          <a
            className="aeonik-base absolute z-[999] translate-y-[-100%] p-4 text-white underline mix-blend-difference focus:translate-y-0 focus:outline-none"
            href="#main-content">
            
            Skip to content
          </a>
          {/* Remove the check once the site is fully migrated to use the Contentful navigation */}
          {isContentfulNavActive ?
          <NavContentful data={navigation} /> :

          <Nav />
          }
          <main id="main-content" className="overflow-x-hidden bg-white">
            <Outlet />
          </main>
          <Footer />
        </RootProvider>

        <ScrollRestoration />
        <script
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: `window.GLOBALS=${GLOBALS}` }} />
        
        <Scripts />
        <LiveReload />
      </body>
    </html>);

}